import React, { useState, useCallback, useRef } from 'react';
import TimelineHover from './TimelineHover.js';
import './TimelineBubble.css';

function TimelineBubble({ type, data, minute, index }) {
  const [showHover, setShowHover] = useState(false);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [hoverBelow, setHoverBelow] = useState(false);
  const bubbleRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const calculatePosition = useCallback((element) => {
    if (!element) return { x: 0, y: 0, below: false };
    
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate center position
    let x = rect.left + rect.width / 2;
    
    // Ensure the hover doesn't go off-screen
    const hoverWidth = 350; // Estimated width of the hover
    const hoverHeight = 200; // Estimated height of the hover
    
    // Calculate minimum and maximum allowed X positions with padding
    const minX = hoverWidth / 2 + 20; // Minimum left edge with padding
    const maxX = viewportWidth - hoverWidth / 2 - 20; // Maximum right edge with padding
    
    // Clamp X position to ensure hover is fully visible
    x = Math.max(minX, Math.min(maxX, x));
    
    // Check if there's enough space above and below
    const spaceAbove = rect.top - hoverHeight;
    const spaceBelow = viewportHeight - rect.bottom - hoverHeight;
    
    // Determine positioning
    let y, below;
    if (spaceAbove < 10 && spaceBelow > spaceAbove + 50) {
      // Not enough space above, but plenty below
      y = rect.bottom + 10;
      below = true;
    } else {
      // Default to above positioning
      y = rect.top - 10;
      below = false;
    }
    
    return { x, y, below };
  }, []);

  const handleMouseEnter = useCallback((e) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    const position = calculatePosition(e.currentTarget);
    setHoverPosition({ x: position.x, y: position.y });
    setHoverBelow(position.below);
    setShowHover(true);
  }, [calculatePosition]);

  const handleMouseLeave = useCallback(() => {
    // Add a small delay to prevent flickering when moving between bubble and hover
    hoverTimeoutRef.current = setTimeout(() => {
      setShowHover(false);
    }, 50);
  }, []);

  const handleHoverEnter = useCallback(() => {
    // Clear the timeout when mouse enters the hover
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  const handleHoverLeave = useCallback(() => {
    // Start the timeout when mouse leaves the hover
    hoverTimeoutRef.current = setTimeout(() => {
      setShowHover(false);
    }, 50);
  }, []);

  const handleClose = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setShowHover(false);
  }, []);

  const getBubbleClass = () => {
    const baseClass = 'timeline-bubble';
    const typeClass = `timeline-bubble-${type}`;
    return `${baseClass} ${typeClass}`.trim();
  };

  const getBubbleContent = () => {
    if (type === 'objective') {
      return 'O';
    } else if (type === 'coaching') {
      return 'C';
    }
    return '';
  };

  const getBubbleTitle = () => {
    if (type === 'objective') {
      return 'Objective';
    } else if (type === 'coaching') {
      return 'Coaching Point';
    }
    return '';
  };

  return (
    <div className="timeline-bubble-container">
      <div
        ref={bubbleRef}
        className={getBubbleClass()}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        title={getBubbleTitle()}
      >
        <span className="timeline-bubble-text">{getBubbleContent()}</span>
      </div>
      
      {showHover && (
        <TimelineHover
          type={type}
          data={data}
          minute={minute}
          position={hoverPosition}
          below={hoverBelow}
          onClose={handleClose}
          onMouseEnter={handleHoverEnter}
          onMouseLeave={handleHoverLeave}
        />
      )}
    </div>
  );
}

export default TimelineBubble;
